import React from 'react';
import { Col, Container, FormGroup, Label, Row } from 'reactstrap';
import { Field, Fields } from 'redux-form';
import { compact } from 'underscore';
import moment from 'moment';

// components
import BufferedInput from '../../../components/form/field/buffered-input';
import BathFilter from '../../../components/form/filter/bath-filter';
import PriceFilter from '../../../components/form/filter/price-filter';
import FurnishedFilter from '../../../components/form/filter/furnished-filter';
import BedFilter from '../../../components/form/filter/bed-filter';
import GarageFilter from '../../../components/form/filter/garage-filter';
import FloorFilter from '../../../components/form/filter/floor-filter';
import YearFilter from '../../../components/form/filter/year-filter';
import { LocationTypeAheadField } from '../../../components/form/field/location-typeahead';
import WaterfrontTypeFilter from '../../../components/form/filter/waterfront-type-filter';
import BuildingTypeFilter from '../../../components/form/filter/building-type-filter';
import SQFTFilter from '../../../components/form/filter/sqft-filter';
import PreferenceFilter from '../../../components/form/filter/preference-filter';
import StatusFilter from '../../../components/form/filter/status-filter';
import CommunityTypeFilter from '../../../components/form/filter/community-type-filter';
import PetFilter from '../../../components/form/filter/pet-filter';
import DateRangeFilter from '../../../components/form/filter/daterange-filter';
import CityFilter from '../../../components/form/filter/city-filter';
import AmenityFilter from '../../../components/form/filter/amenity-filter';

// modules
import BS from '../../../theme/Bootstrap';
import formStyles from '../../../components/form/form.module.scss';
import styles from '../../Containers.module.scss';

const ResidentialAdvancedSearch = props => {
  return (
    <Container className={`${BS.alignItemsStart}`}>
      <Row>
        <Col xs={12} md={4}>
          <FormGroup>
            <Label className={styles.label} for="address">
              Location:
            </Label>
            <LocationTypeAheadField name="address" />
          </FormGroup>
          <FormGroup className={formStyles.inputTextCenter}>
            <Label className={styles.label} for="minPrice">
              Price:
            </Label>
            <Fields
              names={['minPrice', 'maxPrice']}
              component={PriceFilter}
              showSelects
            />
          </FormGroup>
          <FormGroup className={formStyles.inputTextCenter}>
            <Label className={styles.label} for="minBed">
              Bedrooms:
            </Label>
            <Fields
              names={['minBed', 'maxBed']}
              asInput
              component={BedFilter}
              showSuggestions
            />
          </FormGroup>
          <FormGroup className={formStyles.inputTextCenter}>
            <Label className={styles.label} for="numBaths">
              Bathrooms:
            </Label>
            <Field name="numBaths" asInput component={BathFilter} />
          </FormGroup>
          <FormGroup className={formStyles.inputTextCenter}>
            <Label className={styles.label} for="minArea">
              Living Area:
            </Label>
            <Fields names={['minArea', 'maxArea']} component={SQFTFilter} />
          </FormGroup>
          <FormGroup className={formStyles.inputTextCenter}>
            <Label className={styles.label} for="minYear">
              Year Built:
            </Label>
            <Fields names={['minYear', 'maxYear']} component={YearFilter} />
          </FormGroup>
          <FormGroup className={formStyles.inputTextCenter}>
            <Label className={styles.label} for="ohStartDate">
              Open Houses Date Range:
            </Label>
            <Fields
              names={['ohStartDate', 'ohEndDate']}
              labels={['Start Date', 'End Date']}
              component={DateRangeFilter}
              minDate={moment().format('YYYY-MM-DD')}
              showSuggestions
            />
          </FormGroup>
        </Col>
      </Row>
      <FormGroup>
        <Label className={styles.label} for="location">
          City:
        </Label>
        <div>
          <Field
            format={value => {
              return compact((value || '').trim().split(','));
            }}
            normalize={value => {
              return value.join(',');
            }}
            name="city"
            component={CityFilter}
          />
        </div>
      </FormGroup>
      <FormGroup row>
        <Col xs={12} md={4}>
          <Label className={styles.label} for="location">
            MLS ID:
          </Label>
          <div>
            <Field
              name="mlsNumber"
              type="text"
              placeholder="MLS ID"
              component={BufferedInput}
            />
          </div>
        </Col>
      </FormGroup>
      <FormGroup row>
        <Col xs={12} md={4}>
          <Label className={styles.label} for="status">
            Status:
          </Label>
          <Field name="status" component={StatusFilter} />
        </Col>
      </FormGroup>
      <FormGroup>
        <Label className={styles.label} for="furnished">
          Furnished:
        </Label>
        <div>
          <Field
            format={value => {
              return compact((value || '').trim().split(','));
            }}
            normalize={value => {
              return value.join(',');
            }}
            name="furnished"
            component={FurnishedFilter}
          />
        </div>
      </FormGroup>
      <Row>
        <Col xs={12} md={4}>
          <FormGroup className={formStyles.inputTextCenter}>
            <Label className={styles.label} for="minGarageSpaces">
              Garage Spaces:
            </Label>
            <Fields
              names={['minGarageSpaces', 'maxGarageSpaces']}
              asInput
              component={GarageFilter}
            />
          </FormGroup>
          <FormGroup className={formStyles.inputTextCenter}>
            <Label className={styles.label} for="minFloors">
              Total Floors:
            </Label>
            <Fields names={['minFloors', 'maxFloors']} asInput component={FloorFilter} />
          </FormGroup>
        </Col>
      </Row>
      <FormGroup>
        <Label className={styles.label} for="buildingType">
          Building Type:
        </Label>
        <div>
          <Field
            format={value => {
              return compact((value || '').trim().split(','));
            }}
            normalize={value => {
              return value.join(',');
            }}
            name="buildingType"
            component={BuildingTypeFilter}
          />
        </div>
      </FormGroup>
      <FormGroup>
        <Label className={styles.label} for="amenities">
          Amenities:
        </Label>
        <div>
          <Field
            format={value => {
              return compact((value || '').trim().split(','));
            }}
            normalize={value => {
              return value.join(',');
            }}
            name="amenities"
            component={AmenityFilter}
          />
        </div>
      </FormGroup>
      <FormGroup>
        <Label className={styles.label} for="communityType">
          Community Type:
        </Label>
        <div>
          <Field
            format={value => {
              return compact((value || '').trim().split(','));
            }}
            normalize={value => {
              return value.join(',');
            }}
            name="communityType"
            component={CommunityTypeFilter}
          />
        </div>
      </FormGroup>
      <FormGroup>
        <Label className={styles.label} for="pets">
          Pets:
        </Label>
        <div>
          <Field
            format={value => {
              return compact((value || '').trim().split(','));
            }}
            normalize={value => {
              return value.join(',');
            }}
            name="pets"
            component={PetFilter}
          />
        </div>
      </FormGroup>

      <FormGroup row>
        <Col xs={12} md={4}>
          <Label className={styles.label} for="privatePool">
            Private Pool:
          </Label>
          <Field name="privatePool" component={PreferenceFilter} />
        </Col>
      </FormGroup>
      <Row>
        <Col xs={12} md={4}>
          <FormGroup>
            <Label className={styles.label} for="waterfrontYN">
              Waterfront:
            </Label>
            <Field name="waterfrontYN" component={PreferenceFilter} />
          </FormGroup>
        </Col>
      </Row>
      <FormGroup>
        <Label className={styles.label} for="waterfront">
          Waterfront Description:
        </Label>
        <div>
          <Field
            format={value => {
              return compact((value || '').trim().split(','));
            }}
            normalize={value => {
              return value.join(',');
            }}
            name="waterfront"
            component={WaterfrontTypeFilter}
          />
        </div>
      </FormGroup>
      <FormGroup row>
        <Col xs={12} md={4}>
          <Label className={styles.label} for="shortSale">
            Potential Short Sale:
          </Label>
          <Field name="shortSale" component={PreferenceFilter} />
        </Col>
      </FormGroup>
      <FormGroup row>
        <Col xs={12} md={4}>
          <Label className={styles.label} for="foreclosed">
            Foreclosed:
          </Label>
          <Field name="foreclosed" component={PreferenceFilter} />
        </Col>
      </FormGroup>
    </Container>
  );
};

export default ResidentialAdvancedSearch;



// WEBPACK FOOTER //
// ./src/containers/PropertySearch/AdvancedSearch/ResidentialAdvancedSearch.js